import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ClipboardList,
  Search,
  HeartPulse,
  Wind,
  Sparkles,
  Compass,
  Hand,
  Sun,
  Moon,
  ArrowRight,
} from "lucide-react";
import CtaBanner from "@/components/site/CtaBanner";
import UpcomingClasses from "@/components/site/UpcomingClasses";

const included = [
  {
    icon: ClipboardList,
    title: "Goals & Complaints Exploration",
    body: "We begin by exploring what has brought you to therapy — the ache, the pattern, the aspiration. Nothing is too small.",
  },
  {
    icon: Search,
    title: "Comprehensive Assessment",
    body: "A thoughtful, whole-person assessment across physical, mental, emotional, and energetic layers.",
  },
  {
    icon: HeartPulse,
    title: "Guided Yoga Practice",
    body: "A practice tailored specifically to your body and your goals — never a one-size-fits-all class.",
  },
];

const modalities = [
  { icon: HeartPulse, label: "Therapeutic Movement (Āsana)" },
  { icon: Wind, label: "Prāṇāyāma (Breath & Lifeforce)" },
  { icon: Moon, label: "Meditation" },
  { icon: Sun, label: "Relaxation" },
  { icon: Sparkles, label: "Mindfulness" },
  { icon: Hand, label: "Therapeutic Touch" },
  { icon: Compass, label: "Lifestyle Guidance" },
];

const forWho = [
  "Seeking relief from pain",
  "Recovering from injury",
  "Managing stress",
  "Improving mobility",
  "Wanting to feel more balanced and connected",
  "Open to therapeutic touch",
];

const Services = () => {
  return (
    <div data-testid="page-services">
      {/* Header */}
      <section className="pt-40 pb-20 bg-[#FDFCFB]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
            Service
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#2D3A2B] leading-[1.05] tracking-tight max-w-4xl">
            Yoga Therapy — a warm,{" "}
            <span className="italic text-[#4A5D46]">whole-person</span>{" "}
            approach.
          </h1>
          <p className="mt-8 text-xl text-[#4A5D46] leading-relaxed max-w-3xl font-light">
            Individualized sessions to help address the underlying causes of
            imbalance, improve function, reduce pain and stress, and support
            your body&rsquo;s natural capacity to heal.
          </p>
        </div>
      </section>

      {/* What is Included */}
      <section
        data-testid="services-included"
        className="py-24 md:py-32 bg-[#F4F1ED]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-2xl">
            <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
              What&rsquo;s Included
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2D3A2B] leading-tight">
              Every session, thoughtfully <span className="italic">shaped</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {included.map((it, i) => (
              <div
                key={it.title}
                data-testid={`included-card-${i}`}
                className="bg-[#FDFCFB] p-8 rounded-md border border-[#E8E5DF]"
              >
                <div className="w-12 h-12 rounded-full bg-[#F4F1ED] flex items-center justify-center mb-6 text-[#4A5D46]">
                  <it.icon className="w-5 h-5" aria-hidden />
                </div>
                <h3 className="font-serif text-2xl text-[#2D3A2B] mb-3">
                  {it.title}
                </h3>
                <p className="text-sm text-[#4A5D46] leading-relaxed font-light">
                  {it.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modalities */}
      <section
        data-testid="services-modalities"
        className="py-24 md:py-32 bg-[#FDFCFB]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
              The Modalities
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2D3A2B] leading-tight">
              A gentle, integrated <span className="italic">toolkit</span>.
            </h2>
            <p className="mt-8 text-lg text-[#4A5D46] leading-relaxed font-light">
              Your sessions may include therapeutic movement (āsana),
              prāṇāyāma, meditation, relaxation, mindfulness, therapeutic
              touch, lifestyle guidance, and other yogic practices to help
              address the underlying causes of imbalance, improve function,
              reduce pain and stress, and support your body&rsquo;s natural
              capacity to heal.
            </p>
            <div className="mt-8 p-6 bg-[#F4F1ED] rounded-md border-l-2 border-[#C6A692]">
              <p className="font-serif italic text-[#4A5D46] leading-relaxed">
                Prāṇa — lifeforce energy — isn&rsquo;t often spoken about, yet
                without it, we wouldn&rsquo;t be alive. It can be disrupted by
                trauma, stress, not enough movement, hindered breathing, and
                poor sleeping patterns. So much of this work is quietly
                returning that current home.
              </p>
            </div>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {modalities.map((m, i) => (
              <div
                key={m.label}
                data-testid={`modality-item-${i}`}
                className="flex items-center gap-4 p-6 bg-[#F4F1ED] rounded-md hover:bg-[#EBE6DE] transition-colors"
              >
                <span className="w-11 h-11 rounded-full bg-[#FDFCFB] flex items-center justify-center text-[#4A5D46]">
                  <m.icon className="w-5 h-5" aria-hidden />
                </span>
                <span className="font-serif text-xl text-[#2D3A2B]">
                  {m.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section
        data-testid="services-for-who"
        className="py-24 md:py-32 bg-[#2D3A2B]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-16">
            <p className="text-xs tracking-[0.35em] uppercase text-[#C6A692] mb-6">
              Who It&rsquo;s For
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#FDFCFB] leading-tight">
              For anyone seeking{" "}
              <span className="italic text-[#C6A692]">balance</span>, in
              whatever form that takes today.
            </h2>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {forWho.map((w, i) => (
              <li
                key={w}
                data-testid={`for-who-${i}`}
                className="p-6 border border-[#4A5D46] rounded-md text-[#C6BFB4] font-serif text-xl leading-snug hover:border-[#C6A692] hover:text-[#FDFCFB] transition-colors"
              >
                {w}
              </li>
            ))}
          </ul>
          <div className="mt-16 flex flex-col sm:flex-row gap-4">
            <Link to="/pricing" data-testid="services-cta-pricing">
              <Button className="rounded-full bg-[#C6A692] hover:bg-[#B99783] text-[#2D3A2B] px-8 py-6 h-auto">
                See Investment Options
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="services-cta-book">
              <Button
                variant="outline"
                className="rounded-full border-[#C6A692] text-[#C6A692] hover:bg-[#C6A692] hover:text-[#2D3A2B] bg-transparent px-8 py-6 h-auto"
              >
                Book a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <UpcomingClasses />

      <CtaBanner />
    </div>
  );
};

export default Services;
